<script lang="ts" setup>
import { inject, ref } from "vue";
import { EAddressType, IYesNoEnum } from "../ApiInterfaces";
import axios from "axios";
import { ICsvItemsCart, ICsvUploadResponse } from "../interfaces/csv.interface";
import { SITEURL } from "@libTs/cms.globals";
import { IAddressBlock, IOrderCart } from "../interfaces/cart.interfaces";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import { TranslationService } from "../services/translation.service";

const overlaysSvc = inject<OverlaysService>("overlaysSvc")!;
const t = inject<TranslationService>("translationSvc")!.t;

const csvfile = ref<File | null>(null);
const invalidCsvUploadMsg = ref("");
const isOrderForOtherUser = ref(false);
const carts = ref<Array<ICsvItemsCart>>([]);
const isUploading = ref(false);
const customerEmail = ref("");
const customerNumber = ref("");
const invalidLinesStr = ref("");

const shippingAddress = ref<IAddressBlock>({
    Default: IYesNoEnum.N,
    AddressType: EAddressType.S,
    AddressId: "CSV",
    AddressName2: "",
    AddressName3: "",
    Street: "",
    StreetNo: "",
    ZipCode: "0000",
    City: "",
    State: "",
    Country: "",
});

const billingAddress = ref<IAddressBlock>({
    Default: IYesNoEnum.N,
    AddressType: EAddressType.B,
    AddressId: "CSV",
    AddressName2: "",
    AddressName3: "",
    Street: "",
    StreetNo: "",
    ZipCode: "0000",
    City: "",
    State: "",
    Country: "",
});

function uploadFile(): void {
    const data = new FormData();
    const dataExcel = (
        (document.getElementById("csvfile") as HTMLInputElement)
            .files as FileList
    )[0];
    // console.log(dataExcel);

    if (!dataExcel) {
        return;
    }

    isUploading.value = true;

    data.append("file", dataExcel);
    // console.log( data );

    axios
        .post<ICsvUploadResponse>(
            SITEURL + "actions/sapShop/excel/upload",
            data,
            {
                responseType: "json",
                headers: {
                    "Content-Type": undefined,
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .then(
            // success
            (response) => {
                // console.log(response.data);
                if (
                    (response.data.exception &&
                        response.data.exception.length) ||
                    !response.data.customerEmail
                ) {
                    console.error(
                        "Exception in ExcelUpload controller",
                        response.data.exception,
                    );
                    console.warn("invalid customer number in Excel file");
                    invalidCsvUploadMsg.value = response.data.exception || "";
                    return;
                }

                customerEmail.value = response.data.customerEmail;
                customerNumber.value = response.data.customerNumber;

                const resCarts = response.data.carts;

                // console.log(resCarts);

                if (!resCarts) {
                    console.warn("no (grouped) items found in CSV");
                    return;
                }

                for (const key in resCarts) {
                    if (!resCarts.hasOwnProperty(key)) {
                        continue;
                    }
                    const cart: ICsvItemsCart = {
                        orderReference: key === "default" ? "" : key,
                        orderName: resCarts[key].name,
                        currency: resCarts[key].currency,
                        itemsValid: [],
                        itemsInvalid: [],
                    };

                    const items = resCarts[key].items;
                    if (items.length > 0) {
                        for (const item of items) {
                            if (item.valid) {
                                cart.itemsValid.push(item);
                            } else {
                                cart.itemsInvalid.push(item);
                            }
                        }
                        carts.value.push(cart);
                    }
                }

                invalidLinesStr.value = response.data.invalidLinesStr
                    ? response.data.invalidLinesStr
                    : "";
                isOrderForOtherUser.value =
                    response.data.isDifferentCustomer === true;

                // set billing & shipping address
                if (
                    response.data.shippingAddress &&
                    response.data.shippingAddress.City
                ) {
                    const resShippingAddress = response.data
                        .shippingAddress as IAddressBlock;
                    resShippingAddress.AddressId = "CSV";
                    resShippingAddress.Default = IYesNoEnum.Y;
                    resShippingAddress.AddressType = EAddressType.S;

                    if (!resShippingAddress.Street) {
                        resShippingAddress.Street = "n/a";
                    }

                    shippingAddress.value = resShippingAddress;
                }
                if (
                    response.data.billingAddress &&
                    response.data.billingAddress.City
                ) {
                    const resBillingAddress = response.data
                        .billingAddress as IAddressBlock;
                    resBillingAddress.AddressId = "CSV";
                    resBillingAddress.Default = IYesNoEnum.Y;
                    resBillingAddress.AddressType = EAddressType.B;

                    if (!resBillingAddress.Street) {
                        resBillingAddress.Street = "n/a";
                    }

                    billingAddress.value = resBillingAddress;
                }
            },

            // error
            (response) => {
                console.warn(response);
                csvfile.value = null;
                carts.value = [];
            },
        )
        .finally(() => {
            isUploading.value = false;
        });
}

function itemsAddedToCartCallback(): void {
    overlaysSvc.showOverlay(EOverlay.itemsAddedToCart);
}

const addCsvToCart = (): void => {
    if (isUploading.value) {
        return;
    }

    if (invalidLinesStr.value.length > 0) {
        const thpStr =
            "---------------\n" +
            "Excel Upload\n\n" +
            invalidLinesStr.value +
            "\n\n" +
            "---------------";

        const event = new CustomEvent("thpValueUpdate", {
            detail: {
                thpStr: thpStr,
                addToCart: true,
                append: true,
            },
        });
        document.dispatchEvent(event);
    }

    const cartsLocal: Array<IOrderCart> = [];
    for (const cart of carts.value) {
        if (cart.itemsValid.length > 0) {
            cartsLocal.push({
                orderReference: cart.orderReference,
                orderName: cart.orderName,
                currency: cart.currency,
                items: cart.itemsValid,
                count: 0, // updated in CtrlCart
                totalPrice: 0, // updated in CtrlCart
            });
        }
    }

    if (
        shippingAddress.value.Default === IYesNoEnum.Y ||
        billingAddress.value.Default === IYesNoEnum.Y
    ) {
        const event = new CustomEvent("setCheckoutShippingBillingAddress", {
            detail: {
                shippingAddress: shippingAddress.value,
                billingAddress: billingAddress.value,
            },
        });
        document.dispatchEvent(event);
    }

    const event = new CustomEvent("addItemsToCartsEvent", {
        detail: {
            carts: cartsLocal,
            customerEmail: customerEmail.value,
            customerNumber: customerNumber.value,
            isOrderForOtherUser: isOrderForOtherUser.value,
            itemsAddedToCartCallback: itemsAddedToCartCallback,
        },
    });
    document.dispatchEvent(event);

    csvfile.value = null;
    carts.value = [];
};
</script>

<template>
    <div class="overlay-content content-full-width">
        <h2 class="mb-10 text-white">
            {{ t("EXCEL_UPLOAD_TITLE") }}
            <span v-if="csvFlag == 'Test'" style="color: red">(Demo)</span>
        </h2>
        <div class="upload-scrollbar">
            <div
                class="col-sm-offset-2 col-sm-8 col-lg-offset-3 col-lg-6"
                @click.stop
            >
                <form name="csvform" enctype="multipart/form-data" novalidate>
                    <div class="upload mt-8">
                        <label
                            for="csvfile"
                            class="btn-white text-primary"
                            :class="{
                                'btn-grey disabled': isUploading,
                            }"
                            v-text="
                                csvfile && csvfile.name
                                    ? csvfile.name
                                    : t('PRODUCTS_EXCEL_UPLOAD')
                            "
                        ></label>
                        <input
                            class="hidden"
                            type="file"
                            fileupload="csvfile"
                            name="csvfile"
                            id="csvfile"
                            accept=".xlsx"
                            @change="uploadFile"
                        />
                    </div>
                </form>

                <div v-if="invalidCsvUploadMsg" class="pt-4">
                    <!--<span style="color:red;">{{ "CSV_WRONG_CUSTOMER_NUMBER"|t("shop") }}</span>-->
                    <span
                        class="text-red font-bold"
                        v-text="invalidCsvUploadMsg"
                    ></span>
                </div>

                <div v-if="carts.length > 0">
                    <div class="container-fluid mt-8" v-for="cart in carts">
                        <div class="row">
                            <div
                                class="text-white"
                                style="margin-bottom: 0.5em"
                            >
                                <div>
                                    <span class="font-bold mr-2"
                                        >{{
                                            t("PRODUCTS_ORDER_REFERENCE")
                                        }}:</span
                                    >
                                    <span
                                        class="font-bold"
                                        v-text="cart.orderReference"
                                    ></span>
                                </div>
                                <div v-if="cart.orderName">
                                    <span class="font-bold mr-2"
                                        >{{ t("PRODUCTS_ORDER_NAME") }}:</span
                                    >
                                    <span v-text="cart.orderName"></span>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 mb-8">
                            <div class="">
                                <div
                                    class="px-4 flex justify-between items-end mb-2 text-white"
                                >
                                    <div class="font-bold">
                                        {{ t("INVALID_PRODUCTS") }}
                                    </div>
                                    <div class="">
                                        {{ t("QUANTITY") }}
                                    </div>
                                </div>
                                <div
                                    class="px-4"
                                    style="
                                        height: 300px;
                                        border: 1px solid red;
                                        background-color: seashell;
                                    "
                                >
                                    <div
                                        v-for="i in cart.itemsInvalid"
                                        class="px-4 flex justify-between items-center mb-2"
                                    >
                                        <div class="" v-text="i.ItemCode"></div>
                                        <div class="" v-text="i.quantity"></div>
                                    </div>
                                </div>
                            </div>
                            <div class="">
                                <div
                                    class="px-4 flex justify-between items-end mb-2 text-white"
                                >
                                    <div class="font-bold">
                                        {{ t("VALID_PRODUCTS") }}
                                    </div>
                                    <div class="">
                                        {{ t("QUANTITY") }}
                                    </div>
                                </div>
                                <div
                                    class="px-2"
                                    style="
                                        height: 300px;
                                        border: 1px solid green;
                                        background-color: lightgreen;
                                    "
                                >
                                    <div
                                        v-for="i in cart.itemsValid"
                                        class="px-2 flex justify-between items-center mb-2"
                                    >
                                        <div class="" v-text="i.ItemCode"></div>
                                        <div class="" v-text="i.quantity"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <span style="color: red"
                        >* {{ t("PRODUCT_CODE_NOT_FOUND") }}</span
                    ><br />
                    <span style="color: red"
                        >** {{ t("INVALID_QUANTITY") }}</span
                    ><br />
                    <br /><br />

                    <a
                        class="btn-white text-primary"
                        :class="{
                            disabled: isLoading || isUploading,
                            'loading-animation': isLoading,
                        }"
                        @click="addCsvToCart()"
                        >{{ t("ADD_ALL_TO_CART") }}</a
                    >
                </div>
            </div>
        </div>
    </div>
</template>
